import type { Lead } from '../types';

import angular from 'angular';
import $ from 'jquery';

import {
  houseApi,
  leadApi,
  projectApi,
  unitApi,
  UnitStatus,
  userApi,
} from '../services/api';
import { logger } from '../services/logger';
import {
  getSidebarOptions,
  MainSectionBuilder,
  SidebarOptions,
} from './queries';

const regexPhone =
  /^(?:\+?\d{1,3}|0\d{1,3}|00\d{1,2})?(?:\s?\(\d+\))?(?:[-/\s.]|\d)+$/;

export class LeadsCtrl {
  static $inject = [
    '$timeout',
    '$scope',
    '$cookieStore',
    '$state',
    '$translate',
    'localStorageService',
  ];

  /**
   * Track currently active $timeout
   */
  private declare sortByTimeout: any;
  declare options: SidebarOptions;
  declare dynamicGroups: {
    id: number;
    name: string;
    description: string;
    query: string;
    how_often: string;
    is_visible_in_integration?: boolean;
  }[];
  private showOnlyMineWasRemoved = false;
  private offset = 0;
  private limit = 30;

  rawQueries: { param: string[]; main?: string } = {
    param: [],
  };

  query = '';
  lockQuery = false;

  /**
   * Whether we are currently loading leads list
   */
  loadLeads = false;
  showSidebarContent = !$('html').hasClass('-device_tablet');
  dayOfWeekIndex = new Date().getDay();
  showFoucSpinner = false;
  allSmartTasksVisible = false;
  currentUserUsername = '';
  showTxtQuery = false;
  customFieldContructors = {};
  hideSearchLead = true;
  hideSearchMain = true;
  hideSearchNotes = true;
  commonProject = {};
  hideMoreColumns = false;
  fullTextQuery = '';
  userIsAdminRole = false;
  userIsExternalUser = false;
  showMainReservation = false;
  showOnlyMine = false;
  sortByAscending = false;
  showSortByAscending = false;
  showMainMore = false;
  lockMainBlockAfterPaste = false;
  infiniteScrollDisabled = true;
  isFirstLoad = true;
  queryWasCleared = false;
  scrollLeadLoading = false;
  activeGroup = 0;
  onlyMinePart = '';

  leads: Lead[] = [];

  /**
   * Total lead records count for pagination purposes
   */
  total = 0;

  request = {
    fields: ['note', 'unit_info', 'lead_status'],
    sortBy: 'updated_at',
    sortByName: 'Date updated',
    sortByAscending: false,
    query: null,
    main: {} as { [key: string]: any },
    mains: [] as { [key: string]: any }[],
    statuses: [],
    lead: {} as { [key: string]: any },
    deal: {
      price: {
        type: -1,
        value: 1,
      },
      total: {
        type: -1,
        value: 1,
      },
      pricelist: [],
      payment: [],
    },
    notes: {} as { [key: string]: any },
    tasks: {
      quantity: {
        type: -1,
        value: 1,
      },
      added: {
        type: 1,
        value: 1,
      },
      users: [],
      content: '',
    },
  };
  fields: { value: string }[] = [];

  constructor(
    private $timeout: angular.ITimeoutService,
    private $scope: angular.IScope,
    private $cookieStore,
    private $state,
    private $translate: angular.translate.ITranslateService,
    private localStorageService: angular.local.storage.ILocalStorageService,
  ) {
    if ($cookieStore.get('project')) {
      this.commonProject = $cookieStore.get('project');
    }

    const { request } = this;

    this.options = getSidebarOptions({ $translate });

    const template = MainSectionBuilder.getTemplateObject();
    request.main = template.main;
    request.lead = template.lead;
    request.notes = template.notes;

    request.mains.push(request.main);
    request.mains.push(request.lead);
    request.mains.push(request.notes);

    this.initDynamicGroups();
    this.fetchData()
      .then(() => {
        this.initEventHandlers();

        this.buildQuery();

        if (!this.loadLeads) {
          // if this.loadLeads === false this means that buildQuery decided to skip
          // request
          // we won't try to fix legacy logic but just call queryLeads manually
          // in future we need to refactor the whole logic to ensure consistent execution flow
          this.queryLeads();
        }
      })
      .catch((error) => {
        logger.error('Error initializing leads controller', {
          error,
        });
      });
  }

  initEventHandlers() {
    this.$scope.$watch(
      'vm.request.main',
      (_newValue, _oldValue) => {
        this.buildQuery();
      },
      true,
    );

    this.$scope.$watch(
      'vm.request.main.what',
      () => {
        let showMainReservation = false;

        this.request.main.what.value.forEach((item) => {
          if (
            item.value === 'Booked' ||
            item.value === 'Extended reservation' ||
            item.value === 'Canceled reservation'
          ) {
            showMainReservation = true;
          }
        });

        this.showMainReservation = showMainReservation;
      },
      true,
    );

    this.$scope.$watch(
      'vm.request.lead',
      (_newValue, _oldValue) => {
        this.buildQuery();
      },
      true,
    );
    this.$scope.$watch(
      'vm.request.notes',
      (_newValue, _oldValue) => {
        this.buildQuery();
      },
      true,
    );

    this.$scope.$on(
      '$stateChangeSuccess',
      (event, toState, toParams, fromState, _fromParams) => {
        if (
          (fromState.name === 'leads.leadpopup' && toState.name === 'leads') ||
          (fromState.name === 'leads.unitpopup' && toState.name === 'leads') ||
          (fromState.name === 'leads.new' && toState.name === 'leads')
        ) {
          this.queryLeads();
        }
      },
    );
  }

  private async fetchData() {
    const [
      users,
      groups,
      projects,
      houses,
      statuses,
      tags,
      unitStatuses,
      fields,
    ] = await Promise.all([
      userApi.getAll(),
      userApi.getGroups(),
      projectApi.getAll(),
      houseApi.getAllWithSection(),
      leadApi.getStatuses(),
      leadApi.getTags(),
      unitApi.getStatuses(),
      leadApi.getCustomFields(),
    ]);

    const bookedStatuses: UnitStatus[] = [];

    this.options.unitStatuses = unitStatuses;

    angular.forEach(unitStatuses, (status) => {
      if (status.type === '3') {
        bookedStatuses.push(status);
      }
    });

    this.options.main.reservationTypeOptions = bookedStatuses;

    if (bookedStatuses.length > 0) {
      this.request.main.type = bookedStatuses[0].id;
    }

    users.forEach((item, index) => {
      if (item.current) {
        const [firstUser] = users;

        users[0] = item;

        users[index] = firstUser;

        this.currentUserUsername = item.username;
        this.userIsAdminRole = groups.some(
          (group) => group.admin && item.group_id === group.id,
        );
        this.userIsExternalUser = item.role === '1';
      }
    });

    this.options.users = users;
    this.options.usersWho = angular.copy(users);
    this.options.usersUpdated = angular.copy(users);
    this.options.usersCreated = angular.copy(users);
    this.options.usersNoteCreated = angular.copy(users);

    this.onlyMinePart = `lead_updated_by: "${this.options.usersUpdated?.[0]?.username}"`;

    if (this.getLsShowOnlyMine() || this.userIsExternalUser) {
      this.showOnlyMine = true;
      this.showOnlyMineChanged(true);
    }

    const sections: {
      name?: string;
      msGroup?: boolean;
    }[] = [];

    houses.forEach((house) => {
      sections.push({ name: house.name, msGroup: true });

      house.sections.forEach((section) => {
        sections.push(angular.copy(section));
      });

      sections.push({ msGroup: false });
    });

    this.options.main.where.section = sections;

    this.processCustomFields(fields);

    this.options.projects = projects.map((project) => ({
      id: project.id,
      name: project.name,
      selected: false,
    }));

    this.options.leadStatuses = statuses.map((status) => {
      return {
        ...status,
        disabled: status.name.includes('--'),
      };
    });

    this.options.itemsOfInterest = tags.map((tag) => ({
      id: tag.id,
      name: tag.name,
      selected: false,
    }));

    this.$scope.$apply();
  }

  private buildQuery() {
    let query = '(';

    this.request.mains.forEach((main, i) => {
      let mainQuery = '';
      let generatedQuery = '';

      // 4w идет первым
      if (i === 0) {
        mainQuery = '[';
        Object.keys(main).forEach((key) => {
          if (typeof main[key].generateQuery === 'function') {
            const controlQuery = main[key].generateQuery();

            if (controlQuery) {
              if (mainQuery.length > 1) {
                mainQuery += ' & ';
              }

              mainQuery += controlQuery;
            }
          }
        });

        mainQuery += ']';
        this.rawQueries.main = mainQuery;

        // all the rest are going later
      } else if (i > 0) {
        generatedQuery = '{';
        Object.keys(main).forEach((key) => {
          if (typeof main[key].generateQuery === 'function') {
            const controlQuery = main[key].generateQuery();

            if (controlQuery) {
              if (generatedQuery.length > 1) {
                generatedQuery += ' & ';
              }

              generatedQuery += controlQuery;
            }
          }
        });

        generatedQuery += '}';

        if (generatedQuery.length > 2) {
          this.rawQueries.param.push(generatedQuery);
        }
      }

      if (mainQuery && mainQuery.length > 2) {
        query += mainQuery;
      }

      if (generatedQuery && generatedQuery.length > 2) {
        query += generatedQuery;
      }
    });

    // if(request.sortBy && request.sortBy != 'updated_at') {
    //     query += 'sorted: ' + request.sortBy + '_';
    //     query += this.sortByAscending ? 'asc' : 'desc';
    // }

    query += ')';

    if (query === this.query) {
      return;
    }

    if (query.length > 4) {
      this.fullTextQuery = '';
      this.query = query;
    } else {
      // basically query === '()'
      this.query = '';

      if (this.showOnlyMineWasRemoved) {
        this.showOnlyMineWasRemoved = false;
      } else if (this.queryWasCleared && query !== this.query) {
        this.queryWasCleared = false;
      } else {
        return;
      }
    }

    this.offset = 0;

    if (this.activeGroup) {
      // triggers queryLeads() under the hood so we won't duplicate that call here
      this.setQueryToGroup(this.activeGroup, true);
    } else {
      this.queryLeads();
    }
  }

  private async queryLeads(
    params: {
      limit?: number;
      offset?: number;

      /**
       * A callback to call on succesfull request
       *
       * If provided the internal leads assignment will be skipped
       */
      success?: (resp: any) => void;

      /**
       * A callback to call on failed request
       *
       * If provided the internal leads assignment will be skipped
       */
      error?: (error: Error) => void;
    } = {},
  ) {
    if (this.lockQuery) {
      // TODO: this seem to be useless and can be removed in future
      return;
    }

    this.loadLeads = true;
    const { request } = this;

    // Additional columns user turned on
    const fields: string[] = [];

    request.fields.forEach((field) => {
      if (field === 'unit_info' || field === 'lead_status') {
        return;
      }

      fields.push(field);
    });

    try {
      const response = await leadApi.getAll({
        limit: params.limit || 30,
        offset: params.offset || 0,
        query: this.fullTextQuery.length < 4 ? this.query : this.fullTextQuery,
        fields,
        order_by: request.sortBy,
        order: request.sortByAscending === false ? 'desc' : 'asc',
      });

      this.total = Number(response.total);

      if (params.success) {
        params.success(response);
      } else {
        this.leads = response.rows;
      }
    } catch (error) {
      logger.error('Error requesting leads', {
        error,
      });

      if (params.error) {
        params.error(error);
      } else {
        this.leads = error;
      }
    }

    this.loadLeads = false;
    this.infiniteScrollDisabled = false;
    this.$scope.$apply();
  }

  filterFullText = () => {
    if (
      (this.fullTextQuery.length >= 4 && !this.userIsExternalUser) ||
      (this.fullTextQuery.length >= 9 && this.userIsExternalUser)
    ) {
      if (regexPhone.test(this.fullTextQuery) === true) {
        // but need to clean up the phone number off special characters
        this.fullTextQuery = this.fullTextQuery.replace(/\D+/g, '');
      }

      this.queryLeads();
    }
  };

  isFullTextQueryNumber = () => {
    return /^[0-9]+$/.test(this.fullTextQuery);
  };

  clearFullTextQuery = () => {
    if (this.fullTextQuery.length < 4) {
      this.fullTextQuery = '';
    } else {
      this.fullTextQuery = '';
      this.queryLeads();
    }
  };

  focusSearch = () => {
    this.$timeout(() => {
      angular.element('#b-full-text-query').focus();
    }, 100);
  };

  addProjectToCookie = (project) => {
    let cookieProject = null;

    if (this.$cookieStore.get('project')) {
      cookieProject = this.$cookieStore.get('project');
    }

    if (
      project.selected &&
      this.request.lead.project.value.length === 1 &&
      !cookieProject
    ) {
      this.$cookieStore.put('project', project);
    }
  };

  changeQuery = () => {
    if (this.query && this.query.length > 2) {
      this.queryLeads();
    }
  };

  pasteInSearchQuery = () => {
    this.lockMainBlockAfterPaste = true;
    this.hideSearchMain = true;
  };

  infiniteScrollLoadLeads = () => {
    if (this.scrollLeadLoading) {
      return;
    }

    if (this.leads && this.total === this.leads.length) {
      return;
    }

    if (this.offset >= this.total) {
      return;
    }

    this.scrollLeadLoading = true;

    this.offset += 30;

    this.queryLeads({
      limit: this.limit,
      offset: this.offset,
      success: (leads) => {
        leads.rows.forEach((item) => {
          this.leads.push(item);
        });
        this.scrollLeadLoading = false;
      },
      error: () => {
        this.scrollLeadLoading = false;
      },
    });
  };

  setQueryToGroup = (groupId, skipReset = false) => {
    if (this.activeGroup === groupId && !skipReset) {
      this.clearQuery();

      return;
    }

    this.fullTextQuery = '';
    this.activeGroup = groupId;
    this.hideSearchLead = true;
    this.hideSearchMain = true;
    this.hideSearchNotes = true;

    const querySaved = this.dynamicGroups[groupId - 1].query;

    if (this.showOnlyMine) {
      // if we are in "Show only mine" mode we need to append the condition
      // to the existing query
      const position = querySaved.indexOf('})');

      if (position === -1) {
        this.query = [
          querySaved.slice(0, querySaved.length - 1),
          '{',
          this.onlyMinePart,
          '})',
        ].join('');
      } else {
        this.query = [
          querySaved.slice(0, position),
          ' & ',
          this.onlyMinePart,
          querySaved.slice(position),
        ].join('');
      }
    } else {
      this.query = querySaved;
    }

    this.queryLeads();
  };

  markInMultiSelect = (model, idOrStr) => {
    if (isNaN(idOrStr)) {
      model.forEach((item) => {
        // eslint-disable-next-line eqeqeq
        if (item.value == idOrStr) {
          item.selected = true;
        }
      });
    } else {
      model.forEach((item) => {
        // eslint-disable-next-line eqeqeq
        if (item.id == idOrStr) {
          item.selected = true;
        }
      });
    }
  };

  triggerNewLead = () => {
    if (this.fullTextQuery) {
      this.$state.go('leads.new', { phoneOrEmail: this.fullTextQuery });
    }
  };

  showOnlyMineChanged = (showOnlyMineVal: boolean) => {
    this.options.usersUpdated[0].selected = showOnlyMineVal;

    if (showOnlyMineVal) {
      this.localStorageService.set('showOnlyMine', true);
    } else {
      this.showOnlyMineWasRemoved = true;
      this.localStorageService.remove('showOnlyMine');
    }

    if (this.activeGroup) {
      this.setQueryToGroup(this.activeGroup, true);
    }
  };

  updateRequestModel = (proxyValue, type) => {
    switch (type) {
      case 'leadStatus':
        this.request.lead.leadStatus.value = angular.copy(proxyValue);
        break;
      case 'leadProject':
        this.request.lead.project.value = angular.copy(proxyValue);
        break;
      case 'leadTag':
        this.request.lead.tag.value = angular.copy(proxyValue);
        break;
      case 'leadUserUpdated':
        this.request.lead.userUpdated.value = angular.copy(proxyValue);
        break;
    }
  };

  sortBy = (filter, fullName) => {
    this.showSortByAscending = true;
    this.sortByAscending = false;

    this.request.sortBy = filter;
    this.request.sortByName = fullName;

    this.setSortByAscButtonHideTimeout();

    //buildQuery();
    this.queryLeads();
  };

  private setSortByAscButtonHideTimeout() {
    if (this.sortByTimeout) {
      this.$timeout.cancel(this.sortByTimeout);
      this.sortByTimeout = null;
    }

    this.sortByTimeout = this.$timeout(() => {
      this.showSortByAscending = false;
    }, 5000);
  }

  isColumnVisible = (columnValue) => {
    angular.forEach(this.fields, (field) => {
      if (field.value === columnValue) {
        return true;
      }
    });

    return false;
  };

  sortByVisibleColumn = (filter, fullName) => {
    this.showSortByAscending = false;

    if (this.request.sortBy === filter) {
      this.sortByAscending = !this.sortByAscending;
      this.request.sortByAscending = this.sortByAscending;
    } else {
      this.request.sortBy = filter;
      this.request.sortByName = fullName;
    }

    this.queryLeads();
  };

  changeSortByAscending = () => {
    this.sortByAscending = !this.sortByAscending;
    this.request.sortByAscending = this.sortByAscending;

    this.setSortByAscButtonHideTimeout();
    this.queryLeads();
  };

  addColumn = (column) => {
    if (column.selected) {
      this.request.fields.push(column.value);

      // activate table column
      this.options.customFields.forEach((field) => {
        if (column.value === field.value) {
          field.active = true;
        }
      });
    } else {
      this.request.fields.splice(this.request.fields.indexOf(column.value), 1);

      // deactivate table column
      this.options.customFields.forEach((field) => {
        if (column.value === field.value) {
          field.active = false;
        }
      });
    }

    this.queryLeads();
  };

  inputOrKeyUp = ($event, model) => {
    if ($event.keyCode === 27) {
      model.value = null;
      model.enter = false;
    } else if ($event.keyCode === 13) {
      model.enter = true;
      this.buildQuery();
    }
  };

  clearQuery = () => {
    //vm.query = '';
    this.queryWasCleared = true;

    this.request.mains = [];
    const template = angular.copy(MainSectionBuilder.getTemplateObject());
    this.request.main = template.main;
    this.request.lead = template.lead;
    this.request.notes = template.notes;

    this.request.mains.push(this.request.main);
    this.request.mains.push(this.request.lead);
    this.request.mains.push(this.request.notes);

    this.request.lead = angular.extend(
      this.request.lead,
      this.customFieldContructors,
    );

    this.request.sortBy = 'updated_at';
    this.request.sortByName = 'Date updated';
    this.request.sortByAscending = false;

    this.showMainMore = false;
    this.activeGroup = 0;
    this.lockMainBlockAfterPaste = false;
    this.showTxtQuery = false;

    angular.forEach(this.options, (value, key) => {
      if (key === 'columns') {
        // do nothing
      } else if (key === 'customFields') {
        angular.forEach(value, (item) => {
          item.dataMultiSelect.forEach((dataItem) => {
            dataItem.selected = false;
          });
        });
      } else {
        angular.forEach(value, (item) => {
          item.selected = false;
        });
      }
    });

    const deepArrays = [
      this.options.main.where.section!,
      this.options.main.where.type,
      this.options.main.what,
    ];

    deepArrays.forEach((arr) => {
      const tempArr = arr;

      tempArr.forEach((item) => {
        if (!('msGroup' in item)) {
          item.selected = false;
        }
      });

      arr = tempArr;
    });

    if (this.showOnlyMine) {
      this.options.usersUpdated[0].selected = true;
      this.request.lead.userUpdated.value[0] = angular.copy(
        this.options.usersUpdated[0],
      );
    }

    this.buildQuery();
  };

  private processCustomFields(fields) {
    function arrToArrOfObjects(arr: string[]) {
      const newArr: { name: string; disabled: boolean }[] = [];

      if (Array.isArray(arr)) {
        arr.forEach((item) => {
          newArr.push({
            name: item,
            disabled: item.indexOf('--') !== -1,
          });
        });
      }

      return newArr;
    }

    fields.forEach((field) => {
      this.options.columns.push({
        name_translated: field.name,
        value: `custom_field_${field.id}`,
      });

      this.options.customFields.push({
        name: field.name,
        value: `custom_field_${field.id}`,
        slug: field.slug,
        data: field.data,
        dataMultiSelect: arrToArrOfObjects(field.data),
        type: field.type,
        typeLabel: field.typeLabel,
      });

      switch (field.typeLabel) {
        case 'Text':
          this.request.lead[`custom_${field.slug}`] =
            new MainSectionBuilder.TextInputOr(`custom_${field.slug}`);
          break;
        case 'Number':
          this.request.lead[`custom_${field.slug}`] =
            new MainSectionBuilder.SelectQuantity(`custom_${field.slug}`);
          break;
        case 'Choice':
          this.request.lead[`custom_${field.slug}`] =
            new MainSectionBuilder.MultiSelect(`custom_${field.slug}`);
          break;
        case 'Date':
          this.request.lead[`custom_${field.slug}`] =
            new MainSectionBuilder.DateFilter(`custom_${field.slug}`);
          break;
      }
    });

    angular.forEach(this.request.lead, (value, key) => {
      if (key.indexOf('custom_') >= 0) {
        this.customFieldContructors[key] = value;
      }
    });
  }

  private initDynamicGroups() {
    const dynamicGroupsRu = [
      {
        id: 1,
        name: 'Брони, истекающие сегодня',
        description:
          'Выяснить возражения и либо продлить бронь, либо предложить другой вариант.',
        query:
          '([what: "Booked" & expiration: "today" & status: "Active" & lua_type: 1])',
        how_often: 'everyday',
        is_visible_in_integration: true,
      },
      {
        id: 2,
        name: 'Брони, истекающие завтра',
        description:
          'Напомнить о скором снятии брони. Попробовать назначить дату сделки, или выяснить возражения.',
        query:
          '([what: "Booked" & expiration: "1 days from now" & status: "Active"  & lua_type: 1])',
        how_often: 'everyday',
        is_visible_in_integration: true,
      },
      {
        id: 3,
        name: 'Все активные брони',
        description:
          'Мониторинг активных броней. Здесь вы увидите все брони, активные на данный момент',
        query:
          '([what: "Booked" & expiration > "today" & status: "Active" & lua_type: 1])',
        how_often: 'everyday',
        is_visible_in_integration: true,
      },
      {
        id: 4,
        name: 'Переговоры на контроле',
        description:
          'Горячие лиды, которые уже обсуждают конкретные условия. Ответить на все вопросы и назначить дату сделки.',
        query: '({lead_status: "Переговоры по конкретным квартирам"})',
        how_often: 'everyday',
        is_visible_in_integration: false,
      },
      {
        id: 5,
        name: 'Позвонить повторно',
        description:
          'Лиды, последний разговор с которыми не состоялся. Перезвонить при первой возможности.',
        query: '({lead_note: "#позвонить"})',
        how_often: 'everyday',
        is_visible_in_integration: false,
      },
      {
        id: 6,
        name: 'Сформировать шорт-лист домов',
        description:
          'Нужно убедиться, что наши объекты/секции действительно есть в шорт-листе клиента и он будет их рассматривать. По возможности подобрать конкретные квартиры.',
        query:
          '({lead_status: "Спрашивает по домам, отвечаем","Мониторинг цен, неконкретный" & lead_updated > "45 days ago"})',
        how_often: 'twice a week',
      },
      {
        id: 7,
        name: 'Сформировать шорт-лист квартир',
        description:
          'Нужно убедиться, что шорт-лист клиента включает конкретные квартиры из наших домов.',
        query:
          '({lead_status: "Спрашивает по квартирам, отвечаем","Есть шорт-лист домов, секций" & lead_updated > "7 days ago"})',
        how_often: 'twice a week',
      },
      {
        id: 8,
        name: 'Ввести в стадию переговоров',
        description:
          'Мы в шорт-листе, но клиент не обсуждает условия покупки. Стимулировать клиента к обсуждению условий и назначению даты сделки.',
        query:
          '({lead_status: "Есть шорт-лист квартир" & lead_updated > "3 days ago"})',
        how_often: 'twice a week',
      },
      {
        id: 9,
        name: 'Обработать ценовое возражение',
        description:
          'Убедить в ценности бюджетных вариантов, либо предложить рассрочку на желаемый объект инвестирования.',
        query:
          '({lead_status: "Отказ, желаемое - дорого" & lead_updated > "6 days ago"})',
        how_often: 'once a week',
      },
      {
        id: 10,
        name: 'Не подошло местоположение',
        description:
          'Определить критичность местоположения для лида. Либо вернуть в воронку продажи, либо перенести в "Категорический отказ, местоположение"',
        query:
          '({lead_status: "Отказ, местоположение" & lead_updated > "6 days ago"})',
        how_often: 'once a week',
      },
      {
        id: 11,
        name: 'Предложить альтернативы',
        description:
          'Лида не устроили параметры того, что есть в наличии. Пробуем предложить разделение/объединение или убедить пересмотреть критерии. Либо вернуть в воронку продажи, либо перенести в "Категорический отказ, ассортимент"',
        query:
          '({lead_status: "Желаемого по параметрам нет" & lead_updated > "6 days ago"})',
        how_often: 'once a week',
      },
    ];

    const dynamicGroupsEn = [
      {
        id: 1,
        name: 'Expiring reservations',
        description:
          'Find out the objections and either renew the reservation, or suggest another option.',
        query:
          '([what: "Booked" & expiration: "today" & status: "Active" & lua_type: 1])',
        how_often: 'everyday',
      },
      {
        id: 2,
        name: 'Expiring tomorrow',
        description:
          'Remind about soon expiring reservation. Try to set a date of the deal, or find out the objections.',
        query:
          '([what: "Booked" & expiration: "1 days from now" & status: "Active" & lua_type: 1])',
        how_often: 'everyday',
      },
      {
        id: 3,
        name: 'All active reservations',
        description: 'Monitor all active reservations.',
        query: '([what: "Booked" & status: "Active" & lua_type: 1])',
        how_often: 'everyday',
      },
      {
        id: 4,
        name: 'Negotiations under control',
        description:
          'Hot leads who are already discussing specific deal terms. Answer all questions and set a date of the deal.',
        query: '({lead_status: "Negotiation"})',
        how_often: 'everyday',
      },
      {
        id: 5,
        name: 'Call again',
        description: 'Call back those leads at your earliest convenience.',
        query: '({lead_note: "#redial"})',
        how_often: 'everyday',
      },
      {
        id: 6,
        name: 'Define short-list of buildings',
        description:
          "We need to make sure that our buildings are in the lead's short-list and he/she is consider them. If possible, choose specific apartments.",
        query:
          '({lead_status: "Asks about buildings","Unspecific" & lead_updated > "45 days ago"})',
        how_often: 'twice a week',
      },
      {
        id: 7,
        name: 'Define short-list of apartments',
        description:
          "We need to make sure that the lead's short-list includes specific apartments from our buildings.",
        query:
          '({lead_status: "Asks about apartments","Has short-list of buildings" & lead_updated > "7 days ago"})',
        how_often: 'twice a week',
      },
      {
        id: 8,
        name: 'Lead to Negotiation stage',
        description:
          'We are in the short-list, but the lead does not discuss the deal terms. Stimulate the lead to discuss the terms.',
        query:
          '({lead_status: "Has short-list of apartments" & lead_updated > "3 days ago"})',
        how_often: 'twice a week',
      },
      {
        id: 9,
        name: 'Handle a price objection',
        description:
          'Convince in the viability of cheaper options, or offer to pay by installments for the desired apartment.',
        query:
          '({lead_status: "Desired is out of budget" & lead_updated > "6 days ago"})',
        how_often: 'once a week',
      },
      {
        id: 10,
        name: 'Handle a location objection',
        description:
          'Determine how critical the location is for the lead. Either return to the Sales Funnel, or move to "Lost, location"',
        query:
          '({lead_status: "Location bad fit" & lead_updated > "6 days ago"})',
        how_often: 'once a week',
      },
      {
        id: 11,
        name: 'Find alternatives',
        description:
          'Earlier we didn\'t find anything suitable by parameters. We try to propose division / consolidation or persuade to reconsider the criteria. Either return to the Sales Funnel, or move to "Lost, lack of stock"',
        query:
          '({lead_status: "Nothing suitable by parameters" & lead_updated > "6 days ago"})',
        how_often: 'once a week',
      },
    ];

    const dynamicGroupsUk = [
      {
        id: 1,
        name: 'Бронювання до сьогодні',
        description:
          "З'ясувати заперечення, продовжити бронювання або запропонувати альтернативу.",
        query:
          '([what: "Booked" & expiration: "today" & status: "Active" & lua_type: 1])',
        how_often: 'everyday',
        is_visible_in_integration: true,
      },
      {
        id: 2,
        name: 'Бронювання до завтра',
        description:
          "Нагадати щодо бронювання. Спробувати призначити дату підписання або з'ясувати заперечення.",
        query:
          '([what: "Booked" & expiration: "1 days from now" & status: "Active" & lua_type: 1])',
        how_often: 'everyday',
        is_visible_in_integration: true,
      },
      {
        id: 3,
        name: 'Всі активні бронювання',
        description: 'Моніторинг всіх активних бронювань.',
        query: '([what: "Booked" & status: "Active" & lua_type: 1])',
        how_often: 'everyday',
        is_visible_in_integration: true,
      },
      {
        id: 4,
        name: 'Перемовини під контролем',
        description:
          'Гарячі ліди, що вже обговорюють конкретні умови. Призначити дату підписання.',
        query: '({lead_status: "Перемовини"})',
        how_often: 'everyday',
        is_visible_in_integration: false,
      },
      {
        id: 5,
        name: 'Перетелефонувати',
        description: 'Передзвоніть цим лідам як тільки зможете.',
        query: '({lead_note: "#зателефонувати"})',
        how_often: 'everyday',
        is_visible_in_integration: false,
      },
      {
        id: 6,
        name: 'Підібрати список будинків',
        description:
          'Ми маєво впевнитись, що наші будинки у шорт-листу потенційного клиєнта. Якщо можливо, підберіть конкретні квартири.',
        query:
          '({lead_status: "Запитує щодо будинків","Моніторинг цін, неконкретний" & lead_updated > "45 days ago"})',
        how_often: 'twice a week',
      },
      {
        id: 7,
        name: 'Підібрати список квартир',
        description:
          'Ми маєво впевнитись, що квартири з наших будинків є у шорт-листу потенційного клиєнта, що він їх розглядає.',
        query:
          '({lead_status: "Запитує щодо квартир","Має шорт-лист будинків" & lead_updated > "7 days ago"})',
        how_often: 'twice a week',
      },
      {
        id: 8,
        name: 'Підвести до перемовин',
        description:
          'Ми у шорт-листу, але лід не переходить до активних перемовин. Підвести ліда до обговорення умов придбання.',
        query:
          '({lead_status: "Має шорт-лист квартир" & lead_updated > "3 days ago"})',
        how_often: 'twice a week',
      },
      {
        id: 9,
        name: 'Опрацювати цінове заперечення',
        description:
          'Запевнити у цінності більш бюджетних варіантів або запропонувати оплату частинами за варіант, що більш до вподоби.',
        query:
          '({lead_status: "Відмова, бажане не в бюджеті" & lead_updated > "6 days ago"})',
        how_often: 'once a week',
      },
      {
        id: 10,
        name: 'Опрацювати заперечення щодо розташування',
        description:
          'З\'ясувати критичніть місцерозташування ЖК для ліда. Або повернути до воронки продажу, або перемістити до "Категорична відмова, розташування"',
        query:
          '({lead_status: "Відмова, розташування" & lead_updated > "6 days ago"})',
        how_often: 'once a week',
      },
      {
        id: 11,
        name: 'Підібрати альтернативи',
        description:
          'Раніше ми не змогли підібрати гарні варінти за параметрами. Пробуємо запропонувати об\'єднання / розділення квартир або зміну критеріїв. Або повернути до воронки продажу, або перемістити до "Категорична відмова, відсутність варіантів"',
        query:
          '({lead_status: "Немає бажаного за параметрами" & lead_updated > "6 days ago"})',
        how_often: 'once a week',
      },
    ];

    const dynamicGroupsVi = [
      {
        id: 1,
        name: 'Đặt chỗ hết hạn',
        description:
          'Tìm những phản đối và làm mới đặt chỗ hoặc đề xuất lựa chọn khác.',
        query:
          '([what: "Booked" & expiration: "today" & status: "Active" & lua_type: 1])',
        how_often: 'everyday',
        is_visible_in_integration: true,
      },
      {
        id: 2,
        name: 'Hết hạn vào ngày mai',
        description:
          'Nhắc nhở những đặt chỗ sớm hết hạn. Cố gắng sắp xếp ngày giao dịch hoặc tìm những phản đối.',
        query:
          '([what: "Booked" & expiration: "1 days from now" & status: "Active" & lua_type: 1])',
        how_often: 'everyday',
        is_visible_in_integration: true,
      },
      {
        id: 3,
        name: 'Tất cả những đặt chỗ có hiệu lực',
        description: 'Giám sát tất cả đặt chỗ có hiệu lực.',
        query: '([what: "Booked" & status: "Active" & lua_type: 1])',
        how_often: 'everyday',
        is_visible_in_integration: true,
      },
      {
        id: 4,
        name: 'Đàm phán trong tầm kiểm soát',
        description:
          'Khách hàng tiềm năng đã thảo luận điều khoản cụ thể. Hãy trả lời tất cả câu hỏi và sắp xếp lịch giao dịch.',
        query: '({lead_status: "Thương lượng"})',
        how_often: 'everyday',
        is_visible_in_integration: false,
      },
      {
        id: 5,
        name: 'Gọi lại',
        description: 'Gọi lại những khách hàng này khi thuận tiện nhất.',
        query: '({lead_note: "#gọi_lại_sau"})',
        how_often: 'everyday',
        is_visible_in_integration: false,
      },
      {
        id: 6,
        name: 'Định nghĩa danh sách tòa nhà rút gọn',
        description:
          'Chúng ta cần chắc chắn các tòa nhà của chúng ta đang được khách hàng cân nhắc. Nếu có thể, hãy chọn những căn hộ cụ thể.',
        query:
          '({lead_status: "Hỏi về tòa nhà","Không rõ" & lead_updated > "45 days ago"})',
        how_often: 'twice a week',
      },
      {
        id: 7,
        name: 'Định nghĩa danh sách căn hộ rút gọn',
        description:
          'Chúng ta cần chắc chắn khách hàng đang cân nhắc các căn hộ cụ thể trong các tòa nhà của chúng ta.',
        query:
          '({lead_status: "Hỏi về căn hộ","Có danh sách chung cư rút gọn" & lead_updated > "7 days ago"})',
        how_often: 'twice a week',
      },
      {
        id: 8,
        name: 'Đến giai đoạn thương lượng',
        description:
          'Chúng ta đang ở danh sách rút gọn, nhưng khách hàng không bàn về điều khoản giao dịch. Hãy khiến họ thảo luận các điều khoản.',
        query:
          '({lead_status: "Có danh sách căn hộ rút gọn" & lead_updated > "3 days ago"})',
        how_often: 'twice a week',
      },
      {
        id: 9,
        name: 'Xử lý phản đối về giá',
        description:
          'Thuyết phục những lựa chọn rẻ hơn trong khả năng, hoặc đề nghị thanh toán trả góp cho căn hộ khách hàng mong muốn.',
        query:
          '({lead_status: "Mong muốn ngoài ngân sách" & lead_updated > "6 days ago"})',
        how_often: 'once a week',
      },
      {
        id: 10,
        name: 'Xử lý phản đối về địa điểm',
        description:
          'Xác định tầm quan trọng về địa điểm đối với khách hàng. Hoặc chuyển về lại Phễu bán hàng, hoặc chuyển đến "Bỏ lỡ, địa điểm"',
        query:
          '({lead_status: "Địa chỉ không phù hợp" & lead_updated > "6 days ago"})',
        how_often: 'once a week',
      },
      {
        id: 11,
        name: 'Tìm lựa chọn thay thế',
        description:
          'Trước đây chúng ta đã không tìm thấy căn nào phù hợp với tiêu chí. Chúng ta cố gắng đề xuất tách / gộp hoặc thuyết phục khách hàng cân nhắc lại tiêu chí. Hoặc chuyển về lại Phễu bán hàng, hoặc chuyển đến "Bỏ lỡ, thiếu nguồn cung"',
        query:
          '({lead_status: "Không phù hợp tiêu chí nào" & lead_updated > "6 days ago"})',
        how_often: 'once a week',
      },
    ];

    const dynamicGroupsRealtyPro = [
      {
        id: 1,
        name: 'В работе',
        description:
          'Лиды в статусе "Переговоры по сделке" и "Встреча состоялась, принимает решение".',
        query:
          '({lead_status: "Переговоры по сделке","Встреча состоялась, принимает решение" & lead_updated > "1 days ago"})',
        how_often: 'everyday',
        is_visible_in_integration: false,
      },
      {
        id: 2,
        name: 'В работе (брони)',
        description: 'Активные устные брони на 2, 3, 5 дней.',
        query:
          '([what: "Booked" & status: "Active" & lua_type: 1]{unit_status: "Устная бронь 2 дня","Устная бронь 3 дня","Устная бронь 5 дней" & lead_updated > "1 days ago"})',
        how_often: 'everyday',
        is_visible_in_integration: false,
      },
      {
        id: 3,
        name: 'Договоренность о встрече',
        description: 'Лиды в статусе "Мониторинг, планирует приехать"',
        query:
          '({lead_status: "Мониторинг, планирует приехать" & lead_updated > "1 days ago"})',
        how_often: 'everyday',
        is_visible_in_integration: false,
      },
      {
        id: 4,
        name: 'Договоры бронирования',
        description: 'Активные брони по договору бронирования.',
        query:
          '([what: "Booked" & status: "Active" & lua_type: 1]{unit_status: "Договор бронирования" & lead_updated > "1 days ago"})',
        how_often: 'everyday',
        is_visible_in_integration: false,
      },
      {
        id: 5,
        name: 'Позвонить повторно',
        description:
          'Лиды, последний разговор с которыми не состоялся. Перезвонить при первой возможности.',
        query: '({lead_note: "#позвонить"})',
        how_often: 'everyday',
        is_visible_in_integration: false,
      },
      {
        id: 6,
        name: 'Неконкретные',
        description: '',
        query:
          '({lead_status: "Мониторинг, неконкретный","Встреча состоялась, неконкретный" & lead_updated > "6 days ago"})',
        how_often: 'once a week',
      },
    ];

    const currentLang = this.$translate.instant('general.current.lang');

    switch (currentLang) {
      case 'ru':
        this.dynamicGroups = window.location.origin.includes('realtypro')
          ? dynamicGroupsRealtyPro
          : dynamicGroupsRu;
        break;
      case 'en':
        this.dynamicGroups = dynamicGroupsEn;
        break;
      case 'uk':
        this.dynamicGroups = dynamicGroupsUk;
        break;
      case 'vi':
        this.dynamicGroups = dynamicGroupsVi;
        break;
      default:
        // no assignment for other locales
        break;
    }
  }

  private getLsShowOnlyMine() {
    if (this.localStorageService.get('showOnlyMine')) {
      return this.localStorageService.get('showOnlyMine');
    }

    return null;
  }
}
